/**
 * Call the connector administrator grant routes.
 *
 * A grant names one person as the organization's connector administrator for
 * one provider — it covers every workspace using that connector, while
 * workspace data stays behind separate membership. Management calls are
 * organization-admin scoped on the server. The user profile carries the
 * current caller's grants for app-shell discovery.
 */
import type { ConnectorProvider } from './types'

export interface ConnectorAdminGrantsClient {
  get<T>(url: string): Promise<{ data: T }>
  post<T>(url: string, data?: unknown): Promise<{ data: T }>
  delete<T = unknown>(url: string): Promise<{ data: T }>
}

export interface ConnectorAdminGrant {
  orgId: string
  provider: ConnectorProvider
  userId: string
  grantedBy: string
  grantedAt: string
}

export interface ConnectorAdminGrantOptions {
  providers: ConnectorProvider[]
  userIds: string[]
}

const grantsPath = (orgId: string) =>
  `/org/${encodeURIComponent(orgId)}/connector-admin-grants`

export const listConnectorAdminGrants = async (
  api: ConnectorAdminGrantsClient,
  orgId: string,
) => (await api.get<{ grants: ConnectorAdminGrant[] }>(grantsPath(orgId))).data.grants

export const getConnectorAdminGrantOptions = async (
  api: ConnectorAdminGrantsClient,
  orgId: string,
) => (await api.get<ConnectorAdminGrantOptions>(`${grantsPath(orgId)}/options`)).data

export const createConnectorAdminGrant = async (
  api: ConnectorAdminGrantsClient,
  orgId: string,
  provider: ConnectorProvider,
  userId: string,
) => (await api.post<ConnectorAdminGrant>(
  grantsPath(orgId),
  { provider, userId },
)).data

export const revokeConnectorAdminGrant = async (
  api: ConnectorAdminGrantsClient,
  orgId: string,
  provider: ConnectorProvider,
  userId: string,
) => {
  await api.delete(
    `${grantsPath(orgId)}/providers/${encodeURIComponent(provider)}`
    + `/users/${encodeURIComponent(userId)}`,
  )
}

export async function reconcileConnectorAdminGrants(
  api: ConnectorAdminGrantsClient,
  orgId: string,
  userId: string,
  currentProviders: readonly ConnectorProvider[],
  selectedProviders: readonly ConnectorProvider[],
): Promise<void> {
  const current = new Set(currentProviders)
  const selected = new Set(selectedProviders)
  await Promise.all([
    ...selectedProviders
      .filter(provider => !current.has(provider))
      .map(provider => createConnectorAdminGrant(api, orgId, provider, userId)),
    ...currentProviders
      .filter(provider => !selected.has(provider))
      .map(provider => revokeConnectorAdminGrant(api, orgId, provider, userId)),
  ])
}
